import React, { useState } from 'react';
import { Modal as SparkModal, SparkModalPortal, Text } from '@spark';
import ModelSelection from '@/features/model-setup/components/ModelSelection';

const EMPTY_SELECTION = { provider: null, model: null };
const MSG_NO_MODEL = 'Please select a model';
const MSG_DUPLICATE = 'This model is already added to the Playground';

/**
 * Add Model modal. Reuses shared ``ModelSelection`` (Figma commercial model
 * dropdown) so catalog, favorites, and recommended badge match Model Setup.
 * SpecificAI trained versions (ready to deploy / deployed) are included here
 * only — Model configuration does not pass ``includeSpecificAI``.
 *
 * Primary click validates before calling ``onAdd``: empty selection and
 * models already present in the Playground surface a left-aligned critical
 * message above the footer actions.
 */
const AddModelModal = ({
    open,
    onClose,
    onAdd,
    clientId,
    llmUsecaseId,
    taskType,
    existingModels = [],
}) => {
    const [selection, setSelection] = useState(EMPTY_SELECTION);
    const [error, setError] = useState('');

    const handleSelectionChange = (next) => {
        setSelection(next);
        if (error) setError('');
    };

    const handleAdd = () => {
        if (!selection.model) {
            setError(MSG_NO_MODEL);
            return;
        }
        if (existingModels.includes(selection.model)) {
            setError(MSG_DUPLICATE);
            return;
        }
        onAdd(selection);
        setSelection(EMPTY_SELECTION);
        setError('');
    };

    const handleClose = () => {
        setSelection(EMPTY_SELECTION);
        setError('');
        onClose();
    };

    return (
        // Figma 1588:111580 — 520px modal (Spark "text" preset).
        <SparkModalPortal open={open} onClose={handleClose} size="text">
            <SparkModal
                className="pg-add-model-modal fs-unmask"
                title="Add Model"
                showSubtitle={false}
                showScrollBar={false}
                fixedHeight={false}
                showFooter
                primaryLabel="Add Model"
                tertiaryLabel="Cancel"
                onPrimaryClick={handleAdd}
                onTertiaryClick={handleClose}
                onCloseClick={handleClose}
            >
                <div className="pg-add-model-modal__body">
                    <ModelSelection
                        clientId={clientId}
                        llmUsecaseId={llmUsecaseId}
                        taskType={taskType}
                        includeSpecificAI
                        labelText="Select a model"
                        placeholder="Select a model"
                        value={selection}
                        onChange={handleSelectionChange}
                    />
                    {error ? (
                        <Text
                            as="p"
                            variant="detail-regular"
                            className="pg-add-model-modal__error"
                            role="alert"
                        >
                            {error}
                        </Text>
                    ) : null}
                </div>
            </SparkModal>
        </SparkModalPortal>
    );
};

export default AddModelModal;
